import { useEffect, useMemo } from "react";
import { useGetNotificationsQuery } from "../../../common/api/notification";
import { useEstablishmentId } from "../../../common/utils/useEstablishmentId";
import notificationImage from "../../../common/images/notification.svg";
import { mapNotificationToItem } from "../../../common/utils/mapNotificationToItem";
import { useAppSelector } from "../../../app/hooks";

type DashboardNotificationsViewData = {
    head: { title: string; iconUrl: string };
    items: ReturnType<typeof mapNotificationToItem>;
};

type UseDashboardEstablishmentNotificationsResult = {
    loading: boolean;
    isEmpty: boolean;
    viewData?: DashboardNotificationsViewData;
};

export const useDashboardEstablishmentNotifications =
    (): UseDashboardEstablishmentNotificationsResult => {
        const establishmentId = useEstablishmentId();
        const { isCreateCertificateModalOpen } = useAppSelector(
            (state) => state.createCertificateModal,
        );

        const { data, isLoading, isFetching, refetch } =
            useGetNotificationsQuery({
                filter: { establishment_id: establishmentId },
            });

        useEffect(() => {
            if (!isCreateCertificateModalOpen) {
                refetch();
            }
        }, [isCreateCertificateModalOpen, refetch]);

        const loading = isLoading || isFetching;

        const viewData = useMemo(() => {
            if (!data?.data?.length) return undefined;
            return {
                head: {
                    title: "Belangrijke meldingen",
                    iconUrl: notificationImage,
                },
                items: mapNotificationToItem(data.data, 4),
            } as const;
        }, [data]);

        const isEmpty = !loading && !data?.data?.length;

        return { loading, isEmpty, viewData };
    };
